import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import { SEVV_MODAL } from '../VirtualRoom/VirtualRoomModalStyles'
import { Message } from '../../../../types/Messages'
import { getPhaserGameSync } from '../../PhaserGame'
import type Game from '../../scenes/Game'

interface GuestEntry {
  sessionId: string
  name: string
}

interface EscortGuestPanelProps {
  guests: GuestEntry[]
  bookingId: string
  onDismiss: () => void
}

function getRoom() {
  const phaserGame = getPhaserGameSync()
  const game = phaserGame?.scene?.keys?.game as Game | undefined
  return game?.network?.room ?? null
}

/**
 * Panel shown when an internal meeting participant enters reception
 * and there are guests waiting for their meeting.
 */
export default function EscortGuestPanel({ guests, bookingId, onDismiss }: EscortGuestPanelProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(guests.map((g) => g.sessionId)))

  if (guests.length === 0) return null

  const toggleGuest = (sessionId: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(sessionId)) next.delete(sessionId)
      else next.add(sessionId)
      return next
    })
  }

  const handleEscort = () => {
    const room = getRoom()
    if (!room) return
    const ids = Array.from(selected)
    if (ids.length === 0) return
    room.send(Message.ESCORT_START, { guestSessionIds: ids, bookingId })
    onDismiss()
  }

  return (
    <Paper
      elevation={0}
      sx={{
        ...SEVV_MODAL.paper,
        position: 'fixed',
        bottom: 80,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1050,
        minWidth: 280,
        maxWidth: 360,
        p: 0,
      }}
    >
      <Box sx={{ ...SEVV_MODAL.title, padding: '12px 16px' }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Guests in Reception
        </Typography>
      </Box>
      <Box sx={{ padding: '12px 16px' }}>
        {guests.map((g) => (
          <FormControlLabel
            key={g.sessionId}
            control={
              <Checkbox
                checked={selected.has(g.sessionId)}
                onChange={() => toggleGuest(g.sessionId)}
                size="small"
              />
            }
            label={<Typography variant="body2">{g.name}</Typography>}
            sx={{ display: 'flex', mr: 0 }}
          />
        ))}
      </Box>
      <Box sx={{ ...SEVV_MODAL.actions, padding: '10px 16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <Button
          onClick={onDismiss}
          sx={SEVV_MODAL.btnSecondary}
          size="small"
        >
          Dismiss
        </Button>
        <Button
          onClick={handleEscort}
          sx={SEVV_MODAL.btnPrimary}
          size="small"
          disabled={selected.size === 0}
        >
          Escort to Meeting
        </Button>
      </Box>
    </Paper>
  )
}
